import React, { useState, useEffect, useRef } from 'react'

import ReactTooltip from 'react-tooltip'

import { bool, func, node, number, object, oneOf, string } from 'prop-types'

function getOptimalPlacement(element, preferredPlace = 'top') {
  if (!element) return preferredPlace

  const rect = element.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  
  const tooltipWidth = 200
  const tooltipHeight = 50
  const margin = 10

  const spaceTop = rect.top - margin
  const spaceBottom = viewportHeight - rect.bottom - margin
  const spaceLeft = rect.left - margin
  const spaceRight = viewportWidth - rect.right - margin

  const canPlaceTop = spaceTop >= tooltipHeight
  const canPlaceBottom = spaceBottom >= tooltipHeight
  const canPlaceLeft = spaceLeft >= tooltipWidth
  const canPlaceRight = spaceRight >= tooltipWidth

  switch (preferredPlace) {
    case 'top':
      if (canPlaceTop) return 'top'
      if (canPlaceBottom) return 'bottom'
      if (canPlaceLeft) return 'left'
      if (canPlaceRight) return 'right'
      break
    case 'bottom':
      if (canPlaceBottom) return 'bottom'
      if (canPlaceTop) return 'top'
      if (canPlaceLeft) return 'left'
      if (canPlaceRight) return 'right'
      break
    case 'left':
      if (canPlaceLeft) return 'left'
      if (canPlaceRight) return 'right'
      if (canPlaceTop) return 'top'
      if (canPlaceBottom) return 'bottom'
      break
    case 'right':
      if (canPlaceRight) return 'right'
      if (canPlaceLeft) return 'left'
      if (canPlaceTop) return 'top'
      if (canPlaceBottom) return 'bottom'
      break
    default:
      if (canPlaceTop) return 'top'
      if (canPlaceBottom) return 'bottom'
      if (canPlaceLeft) return 'left'
      if (canPlaceRight) return 'right'
  }

  return preferredPlace
}

function Tooltip(props) {
  const {
    autoPosition = false,
    children = null,
    className = '',
    content = null,
    delayHide = 0,
    event,
    href,
    html = false,
    id,
    onClick,
    onShow,
    place = 'top',
    randomiseID = false,
    style,
    tooltipClassName = '',
    wrapperClassName = '',
    clickable = false,
    type = 'light',
  } = props

  const [isOpened, setIsOpened] = useState(false)
  const [calculatedPlace, setCalculatedPlace] = useState(place || 'top')
  const tooltipRef = useRef(null)

  const randomID = randomiseID ? Math.floor(Math.random() * 255) : ''
  const tooltipId = `tooltip-${id + randomID}`

  function hideTooltips() {
    if (!isOpened) {
      ReactTooltip.hide()
    }

    setIsOpened(!isOpened)
  }

  const calculatePosition = () => {
    if (!autoPosition) return

    const anchorElement = document.querySelector(`[data-for="${tooltipId}"]`)
    if (anchorElement) {
      const optimalPlace = getOptimalPlacement(anchorElement, place || 'top')
      setCalculatedPlace(optimalPlace)
    }
  }

  useEffect(() => {
    calculatePosition()
    
    if (autoPosition) {
      const handleResize = () => {
        calculatePosition()
      }
      
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [autoPosition, place, tooltipId])

  return (
    <span className={wrapperClassName}>
      <span
        className={className}
        data-tip
        data-for={tooltipId}
        href={href}
        onClick={onClick}
        style={style}
        data-event={event}
        onMouseDown={() => hideTooltips()}
      >
        {children}
      </span>
      <ReactTooltip
        place={autoPosition ? calculatedPlace : place}
        type={type}
        effect="solid"
        id={tooltipId}
        className={tooltipClassName}
        html={html}
        delayHide={delayHide}
        afterShow={onShow}
        globalEventOff="click"
        clickable={clickable}
        ref={tooltipRef}
      >
        {content}
      </ReactTooltip>
    </span>
  )
}

Tooltip.propTypes = {
  autoPosition: bool,
  children: node,
  className: string,
  content: node,
  delayHide: number,
  event: string,
  href: string,
  html: bool,
  id: string.isRequired,
  onClick: func,
  onShow: func,
  place: oneOf(['top', 'bottom', 'left', 'right']),
  randomiseID: bool,
  style: object,
  tooltipClassName: string,
  wrapperClassName: string,
  clickable: bool,
  type: oneOf(['dark', 'light', 'success', 'warning', 'error', 'info']),
}

export default Tooltip
